import { Calendar } from './Calendar';
import Constants from './Constants';
import DateUtils from './DateUtils';
import FeedUtils from './FeedUtils';
import { FeedItemSubType } from '../models/Feed';
import { CommonFileUtils } from './CommonFileUtils';
export class CommonRfiUtils {
    static COVER_PAGE_ID = 'cover';
    static FEED_PAGE_ID = 'comments';
    static PHOTOS_PAGE_ID = 'photos';
    static PAGE_WIDTH = CommonFileUtils.PAGE_WIDTH;
    static PAGE_HEIGHT = CommonFileUtils.PAGE_HEIGHT;
    static PAGE_HEADER_HEIGHT = CommonFileUtils.PAGE_HEADER_HEIGHT;
    static MARGIN = 48;
    static COLUMN_GAP = 24;
    static FONT = 'Arial, Helvetica, sans-serif';
    static BODY_SIZE = 12;
    static LINE_HEIGHT = 20;
    static LABEL_WIDTH = 120;
    static SECTION_GAP = 14;
    static TABLE_HEADER_HEIGHT = 24;
    static TABLE_ROW_HEIGHT = 24;
    static PHOTO_GAP = 4;
    static FEED_META_SIZE = 8.5;
    static FEED_TEXT_SIZE = 12;
    static FEED_ROW_MIN_HEIGHT = 56;
    /** Photos per generated Photos page; the rest roll onto continuation pages. */
    static MAX_PHOTOS_PER_PAGE = 6;
    static DEFAULT_RFI_WORKFLOW = {
        id: 'default',
        name: 'Default RFI Workflow',
        states: [
            {
                id: 'create',
                pastTense: 'Created',
                action: 'create',
                dueDays: 2,
                dueBy: [],
            },
            {
                id: 'answer',
                action: 'Answer',
                pastTense: 'Answered',
                dueDays: 5,
                dueBy: [],
            },
            {
                id: 'approve',
                action: 'Approve',
                pastTense: 'Approved',
                dueDays: 5,
                dueBy: [],
            },
        ],
    };
    static DEFAULT_RFI_SETTINGS = {
        _id: Constants.SETTINGS_DOC_ID,
        fields: ['distribution', 'tasks', 'tags', 'attachments'],
        workflows: [
            {
                ...CommonRfiUtils.DEFAULT_RFI_WORKFLOW,
                states: CommonRfiUtils.DEFAULT_RFI_WORKFLOW.states.map((s) => ({ ...s, dueBy: [...s.dueBy] })),
            },
        ],
        default_workflow: CommonRfiUtils.DEFAULT_RFI_WORKFLOW.id,
        columns: [],
    };
    static FILE_LIKE_FEED_SUBS = new Set([
        FeedItemSubType.FILE,
        FeedItemSubType.PHOTO,
        FeedItemSubType.DRAWING,
        FeedItemSubType.SPECIFICATION,
    ]);
    static getDbName(projectId) {
        return `p-${projectId}-rfis`;
    }
    static getFeedDbName(projectId, rfiId) {
        return `p-${projectId}-rfi-${rfiId}-feed`;
    }
    static getMarkupsDbName(projectId, rfiId) {
        return `p-${projectId}-rfi-${rfiId}-markups`;
    }
    static isRfiApproved(doc) {
        if (!doc.states?.length || doc.completed_state_index === undefined)
            return false;
        return doc.completed_state_index >= doc.states.length - 1;
    }
    static isDraft(doc) {
        return doc.completed_state_index === undefined;
    }
    static isComplete(doc) {
        if (!doc.states?.length || doc.completed_state_index === undefined)
            return false;
        return doc.completed_state_index >= doc.states.length - 1;
    }
    static getCompletedState(rfi) {
        if (rfi.completed_state_index === undefined || !rfi.states?.length)
            return null;
        return rfi.completed_state_index >= 0 && rfi.completed_state_index < rfi.states.length
            ? rfi.states[rfi.completed_state_index]
            : null;
    }
    /** Feed items included in the RFI (not excluded or deleted). */
    static filterIncludedFeedItems(feed) {
        return (feed || []).filter((item) => !item.excluded && !item.deleted_by && !item.deleted_date);
    }
    /** File ids for included file-like feed items, in feed order. */
    static getIncludedFileIds(feed) {
        const fileIds = [];
        const seen = new Set();
        for (const item of this.filterIncludedFeedItems(feed)) {
            if (!item?.sub_id || !this.FILE_LIKE_FEED_SUBS.has(item.sub) || seen.has(item.sub_id))
                continue;
            seen.add(item.sub_id);
            fileIds.push(item.sub_id);
        }
        return fileIds;
    }
    /** FileDocs for included file-like feed items, in feed order. */
    static getIncludedFileDocs(feed, files) {
        return this.getIncludedFileIds(feed)
            .map((id) => files.find((file) => file._id === id))
            .filter((file) => !!file && !file.deleted_by);
    }
    /** File ids for non-photo file-like feed items (for document pages / PDF append). */
    static getDocumentFileIds(feed) {
        const fileIds = [];
        const seen = new Set();
        for (const item of this.filterIncludedFeedItems(feed)) {
            if (!item?.sub_id || !this.FILE_LIKE_FEED_SUBS.has(item.sub) || item.sub === FeedItemSubType.PHOTO)
                continue;
            if (seen.has(item.sub_id))
                continue;
            seen.add(item.sub_id);
            fileIds.push(item.sub_id);
        }
        return fileIds;
    }
    static getDocumentFileDocs(feed, files) {
        return this.getDocumentFileIds(feed)
            .map((id) => files.find((file) => file._id === id))
            .filter((file) => !!file && !file.deleted_by);
    }
    static getPhotoFeedItems(feed) {
        return this.filterIncludedFeedItems(feed).filter((item) => item.sub === FeedItemSubType.PHOTO);
    }
    static getPhotoFileDocs(feed, files) {
        const seen = new Set();
        const docs = [];
        for (const item of this.getPhotoFeedItems(feed)) {
            if (!item.sub_id || seen.has(item.sub_id))
                continue;
            const file = files.find((f) => f._id === item.sub_id);
            if (!file || file.deleted_by)
                continue;
            seen.add(item.sub_id);
            docs.push(file);
        }
        return docs;
    }
    static getPendingState(rfi) {
        if (!rfi.states?.length)
            return null;
        if (rfi.completed_state_index === undefined)
            return rfi.states[0];
        return rfi.completed_state_index >= 0 && rfi.completed_state_index < rfi.states.length - 1
            ? rfi.states[rfi.completed_state_index + 1]
            : null;
    }
    /** Person ids responsible for the pending workflow state (`dueBy`). */
    static getAssigneeIds(rfi) {
        const pendingState = this.isDraft(rfi) && rfi?.states?.length > 1 ? rfi?.states[1] : this.getPendingState(rfi);
        return pendingState?.dueBy || [];
    }
    static getNextState(rfi) {
        if (!rfi.states?.length)
            return null;
        if (rfi.completed_state_index === undefined)
            return rfi.states[1] || null;
        return rfi.completed_state_index >= 0 && rfi.completed_state_index < rfi.states.length - 2
            ? rfi.states[rfi.completed_state_index + 2]
            : null;
    }
    static filterStatusFeedItems(feed) {
        return (feed || []).filter((item) => item.sub === FeedItemSubType.STATUS && !item.deleted_by && !item.deleted_date);
    }
    static getPreviousStatusFeedItem(feed) {
        return this.filterStatusFeedItems(feed).sort(FeedUtils.sortNewestFirst).at(0) || null;
    }
    static getStateDueDate(state, previousDoneDate, calendar) {
        if (!state || !state.dueDays || state.dueDays <= 0)
            return previousDoneDate;
        const durationInSeconds = calendar.workingDaysToSeconds(state.dueDays);
        return calendar.getDateFromPositiveDuration(previousDoneDate, durationInSeconds);
    }
    /**
     * Due date for the current (pending) workflow state:
     * `completed_state_date` + current state's `dueDays` on the given calendar.
     */
    static getPendingStateDueDate(rfi, calendar) {
        const state = this.getPendingState(rfi);
        if (!state || !rfi.completed_state_date)
            return null;
        const previousDoneDate = DateUtils.fromISO(rfi.completed_state_date);
        if (!previousDoneDate)
            return null;
        return this.getStateDueDate(state, previousDoneDate, calendar);
    }
    /**
     * Human-readable workflow sentences for the cover page summary section.
     * Completed/return STATUS items first (oldest→newest), then pending states.
     */
    static getWorkflowSummarySentences(rfi, people, feed, calendar) {
        const states = rfi.states || [];
        const statusItemsAsc = this.filterIncludedFeedItems(feed)
            .filter((item) => item.sub === FeedItemSubType.STATUS)
            .sort(FeedUtils.sortOldestFirst);
        const sentences = [];
        let completedIndex = -1;
        let previousDoneDate = null;
        let previousStatusUserId;
        for (const item of statusItemsAsc) {
            const completedDate = DateUtils.fromISO(item.date);
            const dateLabel = completedDate ? DateUtils.formatDayOfMonth(completedDate) : '';
            const nextIndex = item.sub_id ? states.findIndex((s) => s.id === item.sub_id) : -1;
            // Moving to an earlier (or draft) index means the workflow went backwards.
            const isReturn = nextIndex < completedIndex;
            if (isReturn) {
                const pendingState = completedIndex + 1 < states.length ? states[completedIndex + 1] : null;
                const actor = this.personDisplayName(people, item.user_id);
                const backToPerson = this.personDisplayName(people, previousStatusUserId);
                const lateSuffix = this.formatLateSuffix(pendingState, previousDoneDate, completedDate, calendar, item.data?.dueDate);
                const messageSuffix = item.text?.trim() ? ` with the message: ` : '';
                sentences.push({
                    personId: item.user_id,
                    summary: `${actor} returned the RFI to ${backToPerson} on the ${dateLabel} ${lateSuffix} ${messageSuffix}`,
                    message: item.text?.trim() || undefined,
                });
                completedIndex = nextIndex;
            }
            else {
                const state = nextIndex >= 0 ? states[nextIndex] : null;
                const person = this.personDisplayName(people, item.user_id);
                const pastTense = state?.pastTense || 'Updated';
                const lateSuffix = this.formatLateSuffix(state, previousDoneDate, completedDate, calendar, item.data?.dueDate);
                sentences.push({
                    personId: item.user_id,
                    summary: `${person} ${pastTense} the RFI on the ${dateLabel}.${lateSuffix}`,
                    message: item.text?.trim() || undefined,
                });
                if (nextIndex >= 0)
                    completedIndex = nextIndex;
            }
            previousStatusUserId = item.user_id;
            if (completedDate)
                previousDoneDate = completedDate;
        }
        const pendingStates = states.slice((rfi.completed_state_index ?? -1) + 1);
        if (!pendingStates.length)
            return sentences;
        const lastStatus = statusItemsAsc[statusItemsAsc.length - 1];
        let anchorDate = lastStatus ? DateUtils.fromISO(lastStatus.date) : DateUtils.currentDate;
        for (let index = 0; index < pendingStates.length; index++) {
            const state = pendingStates[index];
            const dueDate = this.getStateDueDate(state, anchorDate, calendar);
            if (index === 0) {
                anchorDate = new Date(Math.max(dueDate.getTime(), DateUtils.currentDate.getTime()));
            }
            else {
                anchorDate = dueDate;
            }
            const dueByIds = state.dueBy || [];
            const dueByNames = dueByIds
                .map((id) => this.findPerson(people, id))
                .filter((person) => !!person)
                .map((person) => this.getFullName(person))
                .join(' or ');
            const who = dueByNames || 'Someone';
            const action = state.action || 'update';
            sentences.push({
                personId: dueByIds[0],
                summary: `${who} must ${action} the RFI before the ${DateUtils.formatDayOfMonth(dueDate)}.`,
            });
        }
        return sentences;
    }
    static personDisplayName(people, personId) {
        if (!personId)
            return 'Unknown User';
        const person = this.findPerson(people, personId);
        return person ? this.getFullName(person) : 'Unknown User';
    }
    /** Working days late after due (0 if on-time or early). Inclusive calendar count minus 1. */
    static formatLateSuffix(state, previousDoneDate, completedDate, calendar, storedDueDate) {
        if (!completedDate)
            return '';
        let dueDate = null;
        if (storedDueDate) {
            dueDate = DateUtils.fromISO(storedDueDate);
        }
        else if (state && previousDoneDate) {
            dueDate = this.getStateDueDate(state, previousDoneDate, calendar);
        }
        else if (state && !previousDoneDate) {
            // First status (e.g. Create): treat completion date as due so late = 0.
            dueDate = completedDate;
        }
        if (!dueDate)
            return '';
        const inclusive = calendar.workDaysBetween(dueDate, completedDate);
        const deltaDays = inclusive > 0 ? inclusive - 1 : 0;
        return deltaDays > 0 ? ` (${deltaDays} days overdue)` : '';
    }
    static get columnWidth() {
        return Math.floor((this.PAGE_WIDTH - this.MARGIN * 2 - this.COLUMN_GAP) / 2);
    }
    static get rightColumnX() {
        return this.MARGIN + this.columnWidth + this.COLUMN_GAP;
    }
    /**
     * The generated SVG pages in export order — cover, photos, comments — with footer
     * numbering derived from the pages actually produced. Photo and comment pages are
     * omitted when there is nothing to show, and photos spill onto continuation pages.
     *
     * Two passes: cover content is laid out first because it can span several pages, so
     * the total is only known once every page exists; footers are rendered afterwards.
     */
    static getGeneratedPages(options) {
        const renderablePhotos = (options.photos || []).filter((photo) => this.hasPhotoImage(photo, options.photoImages));
        const photoPages = this.chunkPhotos(renderablePhotos);
        const comments = options.comments || [];
        const coverContents = this.layoutCoverPages(options);
        const totalPages = coverContents.length + photoPages.length + (comments.length ? 1 : 0);
        const pages = coverContents.map((content, coverPageIndex) => this.renderCoverPage(options, {
            content,
            coverPageIndex,
            coverPageCount: coverContents.length,
            pageNumber: coverPageIndex + 1,
            totalPages,
        }));
        photoPages.forEach((photos, photoPageIndex) => {
            pages.push(this.getPhotosPage({
                ...options,
                photos,
                photoPageIndex,
                photoPageCount: photoPages.length,
                pageNumber: pages.length + 1,
                totalPages,
            }));
        });
        if (comments.length) {
            pages.push(this.getCommentsPage({ ...options, comments, pageNumber: pages.length + 1, totalPages }));
        }
        return pages;
    }
    /** Photos grouped per page; empty when there are no photos to show. */
    static chunkPhotos(photos) {
        const chunks = [];
        for (let index = 0; index < photos.length; index += this.MAX_PHOTOS_PER_PAGE) {
            chunks.push(photos.slice(index, index + this.MAX_PHOTOS_PER_PAGE));
        }
        return chunks;
    }
    /** First page keeps the plain `photos` id so existing markups still resolve. */
    static getPhotosPageId(photoPageIndex) {
        return photoPageIndex > 0 ? `${this.PHOTOS_PAGE_ID}-${photoPageIndex + 1}` : this.PHOTOS_PAGE_ID;
    }
    /** First page keeps the plain `cover` id so existing markups still resolve. */
    static getCoverPageId(coverPageIndex) {
        return coverPageIndex > 0 ? `${this.COVER_PAGE_ID}-${coverPageIndex + 1}` : this.COVER_PAGE_ID;
    }
    /** Cover pages, numbered from `options.pageNumber`; overflow flows onto extra pages. */
    static getCoverPages(options) {
        const contents = this.layoutCoverPages(options);
        return contents.map((content, coverPageIndex) => this.renderCoverPage(options, {
            content,
            coverPageIndex,
            coverPageCount: contents.length,
            pageNumber: options.pageNumber + coverPageIndex,
            totalPages: options.totalPages,
        }));
    }
    /**
     * Pass 1 — place cover content, returning one body svg per page. The two field
     * columns are fixed to the top of the first page; everything below them flows.
     */
    static layoutCoverPages(options) {
        const { rfi, people, feed = [], calendar } = options;
        const width = this.PAGE_WIDTH - this.MARGIN * 2;
        const leftColumn = CommonFileUtils.renderFieldColumn(this.getRequiredColumnFields(rfi, people, options), this.MARGIN, CommonFileUtils.BODY_Y, this.columnWidth, { labelWidth: this.LABEL_WIDTH });
        const rightColumn = CommonFileUtils.renderFieldColumn(this.getOptionalColumnFields(rfi, options), this.rightColumnX, CommonFileUtils.BODY_Y, this.columnWidth, { labelWidth: this.LABEL_WIDTH });
        const sentences = this.getWorkflowSummarySentences(rfi, people, feed, calendar ?? Calendar.getDefaultCalendar());
        const blocks = [
            ...this.getFullWidthFields(options, rfi, feed, people).map((field) => ({
                render: (y) => CommonFileUtils.renderFieldColumn([field], this.MARGIN, y, width, { labelWidth: this.LABEL_WIDTH }),
                split: (available) => this.splitFieldBlocks(field, width, available),
            })),
            {
                gapBefore: this.SECTION_GAP + 8,
                render: (y) => this.renderWorkflowSummarySection(sentences, people, this.MARGIN, y, width),
                // Only used when the whole workflow cannot fit on a page by itself.
                split: () => sentences.map((sentence, index) => ({
                    render: (y) => this.renderWorkflowSummarySection([sentence], people, this.MARGIN, y, width, {
                        showTitle: index === 0,
                        idOffset: index,
                    }),
                })),
            },
        ];
        const pages = this.flowBlocks(blocks, [leftColumn.svg, rightColumn.svg], {
            startY: Math.max(leftColumn.endY, rightColumn.endY) + 8,
            maxY: CommonFileUtils.CONTENT_MAX_Y,
        });
        return pages.map((parts) => parts.join('\n  '));
    }
    /**
     * Breaks a field too tall for one page into chunks: the first fills `available`
     * height where the field starts, the rest fill whole pages. Chunk lines are already
     * wrapped to `width`, so re-rendering them re-wraps identically.
     */
    static splitFieldBlocks(field, width, available) {
        const lines = this.wrapText(field.value, width, CommonFileUtils.BODY_SIZE);
        // A line of the budget goes to the label that sits above the value. Bordered
        // fields also spend space on the gap above the box, inner pad, and descenders.
        const borderExtra = field.border ? CommonFileUtils.BODY_SIZE + 3 * CommonFileUtils.BORDER_PAD : 0;
        const linesThatFit = (height) => Math.floor((height - borderExtra) / CommonFileUtils.LINE_HEIGHT) - 1;
        const firstCount = linesThatFit(available);
        const pageCount = Math.max(1, linesThatFit(CommonFileUtils.CONTENT_MAX_Y - CommonFileUtils.BODY_Y));
        const firstTake = firstCount > 0 ? firstCount : pageCount;
        // Nothing gained if every line lands in the first chunk.
        if (lines.length <= firstTake)
            return [];
        const blocks = [];
        let index = 0;
        let take = firstTake;
        while (index < lines.length) {
            const chunk = {
                label: index === 0 ? field.label : `${field.label} (cont.)`,
                value: lines.slice(index, index + take).join('\n'),
                multiline: true,
                border: field.border,
            };
            blocks.push({
                render: (y) => CommonFileUtils.renderFieldColumn([chunk], this.MARGIN, y, width, { labelWidth: this.LABEL_WIDTH }),
            });
            index += take;
            take = pageCount;
        }
        return blocks;
    }
    /**
     * Places each block whole, moving it to a fresh page when it would cross `maxY`. A
     * block too tall for a page of its own is split where it stands; if it cannot be
     * split it is placed as-is, so content overflows rather than disappearing.
     */
    static flowBlocks(blocks, firstPageParts, bounds) {
        const pages = [[...firstPageParts]];
        let cursorY = bounds.startY;
        const place = (block) => {
            const page = pages[pages.length - 1];
            const y = page.length ? cursorY + (block.gapBefore || 0) : cursorY;
            const placed = block.render(y);
            if (placed.endY <= bounds.maxY) {
                page.push(placed.svg);
                cursorY = placed.endY;
                return;
            }
            const fitsOwnPage = block.render(CommonFileUtils.BODY_Y).endY <= bounds.maxY;
            if (fitsOwnPage) {
                pages.push([]);
                cursorY = CommonFileUtils.BODY_Y;
                place(block);
                return;
            }
            const parts = block.split?.(bounds.maxY - y);
            if (parts?.length) {
                parts.forEach(place);
                return;
            }
            page.push(placed.svg);
            cursorY = placed.endY;
        };
        blocks.forEach(place);
        return pages;
    }
    /** Pass 2 — wrap laid-out content in the page chrome now that numbering is known. */
    static renderCoverPage(options, page) {
        const { rfi, project, company } = options;
        const { content, coverPageIndex, coverPageCount, pageNumber, totalPages } = page;
        const title = coverPageCount > 1 ? `Cover ${coverPageIndex + 1} of ${coverPageCount}` : 'Cover';
        return {
            _id: this.getCoverPageId(coverPageIndex),
            file_id: rfi._id,
            title,
            thumb_width: this.PAGE_WIDTH,
            thumb_height: this.PAGE_HEIGHT,
            body: `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  ${CommonFileUtils.renderHeader(company, project)}
  ${CommonFileUtils.renderTitle(this.escapeXml(`RFI #${rfi.code}: ${rfi.subject ?? ''}`))}
  ${content}
  ${CommonFileUtils.renderFooter(company, pageNumber, totalPages)}
</svg>`,
        };
    }
    /** Comments-only feed page (photos stay on the separate Attachments page). */
    static getCommentsPage(options) {
        const { rfi, people, project, company, comments = [], pageNumber, totalPages } = options;
        const x = this.MARGIN;
        const width = this.PAGE_WIDTH - this.MARGIN * 2;
        const tableY = CommonFileUtils.BODY_Y;
        const commentEntries = this.getRenderableCommentEntries(comments.sort(FeedUtils.sortOldestFirst), people);
        let contentY = tableY + 10;
        const rowParts = [];
        for (let i = 0; i < commentEntries.length; i++) {
            const entry = commentEntries[i];
            const metaLines = this.wrapText(entry.meta, width - 20, this.FEED_META_SIZE);
            const textLines = this.wrapText(entry.text, width - 20, this.FEED_TEXT_SIZE).slice(0, 4);
            const metaHeight = Math.max(1, metaLines.length) * 11;
            const textHeight = Math.max(1, textLines.length) * 16;
            const rowHeight = Math.max(this.FEED_ROW_MIN_HEIGHT, 8 + metaHeight + textHeight + 8);
            const metaSvg = metaLines
                .map((line, index) => {
                const lineY = contentY + 11 + index * 11;
                return `<text x="${x + 8}" y="${lineY}" font-family="${this.FONT}" font-size="${this.FEED_META_SIZE}" fill="#4b4b4b">${this.escapeXml(line)}</text>`;
            })
                .join('\n  ');
            const textBaseY = contentY + 11 + metaHeight + 4;
            const textSvg = textLines
                .map((line, index) => {
                const lineY = textBaseY + index * 16;
                return `<text x="${x + 8}" y="${lineY}" font-family="${this.FONT}" font-size="${this.FEED_TEXT_SIZE}" fill="#222222">${this.escapeXml(line)}</text>`;
            })
                .join('\n  ');
            const separator = i < commentEntries.length - 1
                ? `<line x1="${x}" y1="${contentY + rowHeight}" x2="${x + width}" y2="${contentY + rowHeight}" stroke="#b8b8b8" stroke-width="1"/>`
                : '';
            rowParts.push(`${metaSvg}
  ${textSvg}
  ${separator}`);
            contentY += rowHeight;
        }
        const tableHeight = Math.max(this.FEED_ROW_MIN_HEIGHT, contentY - tableY);
        const commentsSvg = `<rect x="${x}" y="${tableY}" width="${width}" height="${tableHeight}" fill="#ffffff" stroke="#6f6f6f" stroke-width="1"/>
  ${rowParts.join('\n  ')}`;
        return {
            _id: this.FEED_PAGE_ID,
            file_id: rfi._id,
            title: 'Comments',
            thumb_width: this.PAGE_WIDTH,
            thumb_height: this.PAGE_HEIGHT,
            body: `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  ${CommonFileUtils.renderHeader(company, project)}
  ${CommonFileUtils.renderTitle('Comments')}
  ${commentsSvg}
  ${CommonFileUtils.renderFooter(company, pageNumber, totalPages)}
</svg>`,
        };
    }
    static getPhotosPage(options) {
        const { rfi, people, project, company, photos, photoImages, photoPageIndex = 0, photoPageCount = 1, pageNumber, totalPages, } = options;
        const x = this.MARGIN;
        const width = this.PAGE_WIDTH - this.MARGIN * 2;
        const photoEntries = this.getRenderablePhotoEntries(photos, people, photoImages);
        const photosSvg = this.renderPhotoGrid(photoEntries, x, CommonFileUtils.BODY_Y, width);
        const title = photoPageCount > 1 ? `Photos ${photoPageIndex + 1} of ${photoPageCount}` : 'Photos';
        return {
            _id: this.getPhotosPageId(photoPageIndex),
            file_id: rfi._id,
            title,
            thumb_width: this.PAGE_WIDTH,
            thumb_height: this.PAGE_HEIGHT,
            body: `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  ${CommonFileUtils.renderHeader(company, project)}
  ${CommonFileUtils.renderTitle(this.escapeXml(title))}
  ${photosSvg}
  ${CommonFileUtils.renderFooter(company, pageNumber, totalPages)}
</svg>`,
        };
    }
    static getRenderableCommentEntries(feed, people) {
        if (!feed.length) {
            return [{ meta: 'No comments', text: 'No comments for this RFI.' }];
        }
        return feed.map((item) => {
            const person = this.findPerson(people, item.user_id);
            const personName = person ? this.getFullName(person) : 'Unknown User';
            const company = person?.company ? `, ${person.company}` : '';
            const date = item.date ? DateUtils.formatIso(item.date) : '';
            const meta = [personName + company, date].filter(Boolean).join('  •  ');
            return { meta, text: item.text || '' };
        });
    }
    static getOptionalColumnFields(rfi, _options) {
        const enabledFields = _options.settings?.fields || [];
        const tagsLabel = (rfi.tags || [])
            .map((t) => t.name)
            .filter(Boolean)
            .join(', ');
        const costCodeLable = rfi.cost_code || '';
        return [
            { key: 'priority', label: 'Priority', value: rfi.priority },
            { key: 'cost-code', label: 'Cost Code', value: costCodeLable },
            { key: 'cost-impact', label: 'Cost Impact', value: rfi.cost_impact },
            { key: 'schedule-impact', label: 'Schedule Impact', value: rfi.schedule_impact },
            { key: 'tags', label: 'Tags', value: tagsLabel },
        ]
            .filter((field) => enabledFields.includes(field.key) || (field.value != null && String(field.value).trim() !== ''))
            .map(({ label, value }) => ({ label, value: value ?? '' }));
    }
    static getRequiredColumnFields(rfi, people, options) {
        const completedState = this.getCompletedState(rfi);
        const pendingStatus = this.getPendingState(rfi);
        const stateDueDate = this.getPendingStateDueDate(rfi, options.calendar ?? Calendar.getDefaultCalendar());
        return [
            { label: 'Current Status', value: completedState?.pastTense || 'Draft' },
            { label: 'Pending Action', value: pendingStatus?.pastTense || 'None' },
            { label: 'Pending Action Due Date', value: stateDueDate ? DateUtils.format(stateDueDate) : '' },
            { label: 'RFI Due Date', value: rfi.due_date ? DateUtils.formatIso(rfi.due_date) : '' },
        ];
    }
    /** Resolve successor task display names; prefer provided labels, else fall back to Task {id}. */
    static getSuccessorTaskLabels(rfi, taskLabels) {
        if (taskLabels?.length)
            return taskLabels;
        return (rfi.successors || [])
            .map((s) => s.task_unique_id)
            .filter((id) => id != null)
            .map((id) => `Task ${id}`);
    }
    static resolveSuccessorTaskLabels(rfi, tasks) {
        return (rfi.successors || [])
            .map((s) => s.task_unique_id)
            .filter((id) => id != null)
            .map((id) => {
            const task = tasks.find((t) => t.unique_id === id);
            return task?.name?.trim() || `Task ${id}`;
        });
    }
    static getFullWidthFields(_options, rfi, feed = [], people = []) {
        const fields = [];
        const pendingStatus = this.getPendingState(rfi);
        if (pendingStatus?.dueBy?.length) {
            const pluralSuffix = pendingStatus?.dueBy && pendingStatus?.dueBy?.length > 1 ? 's' : '';
            const assignees = this.formatPeopleList(people, pendingStatus?.dueBy);
            fields.push({ label: 'Assignee' + pluralSuffix, value: assignees, multiline: true });
        }
        const taskLabels = this.getSuccessorTaskLabels(rfi, _options.taskLabels);
        if (taskLabels.length) {
            fields.push({ label: 'Affected Tasks', value: taskLabels.join(', '), multiline: true });
        }
        if (rfi.distribution?.length) {
            fields.push({ label: 'Distribution', value: this.formatPeopleList(people, rfi.distribution), multiline: true });
        }
        if (rfi.description?.trim()) {
            fields.push({ label: 'Description', value: rfi.description, multiline: true });
        }
        if (rfi.question?.trim() || _options.settings?.fields?.includes('question')) {
            fields.push({ label: 'Question', value: rfi.question || '-', multiline: true });
        }
        if (rfi.suggested_answer?.trim() || _options.settings?.fields?.includes('suggested-answer')) {
            fields.push({ label: 'Suggested Answer', value: rfi.suggested_answer || '-', multiline: true });
        }
        if (rfi.accepted_answer?.trim()) {
            fields.push({ label: 'Accepted Answer', value: rfi.accepted_answer, multiline: true, border: '#c62828' });
        }
        return fields;
    }
    /** Final-state STATUS feed item text (set when completing the last workflow state). */
    static getAcceptedAnswerFromFeed(rfi, feed) {
        if (!this.isComplete(rfi) || !rfi.states?.length)
            return null;
        const lastStateId = rfi.states[rfi.states.length - 1]?.id;
        if (!lastStateId)
            return null;
        const statusItem = this.filterStatusFeedItems(feed)
            .filter((item) => item.sub_id === lastStateId && !!item.text?.trim())
            .sort(FeedUtils.sortNewestFirst)
            .at(0);
        return statusItem?.text?.trim() || null;
    }
    /**
     * Workflow sentences under a "Workflow" heading. `showTitle` is false for
     * continuation chunks, and `idOffset` keeps avatar clip ids unique within a page.
     */
    static renderWorkflowSummarySection(sentences, people, x, y, width, options = {}) {
        const { showTitle = true, idOffset = 0 } = options;
        const titleY = y;
        const fontSize = this.BODY_SIZE;
        const lineHeight = this.LINE_HEIGHT;
        const avatarSize = 25;
        const avatarGap = 8;
        const textX = x + avatarSize + avatarGap;
        const textWidth = Math.max(40, width - avatarSize - avatarGap);
        let cursorY = showTitle ? titleY + 28 : titleY;
        const lineSvgs = [];
        const defs = [];
        for (let rowIndex = 0; rowIndex < sentences.length; rowIndex++) {
            const entry = sentences[rowIndex];
            const wrapped = this.wrapText(entry.summary, textWidth, fontSize);
            const quotedMessage = entry.message ? `"${entry.message}"` : '';
            const messageLines = quotedMessage
                ? this.wrapText(quotedMessage, textWidth, this.FEED_TEXT_SIZE).slice(0, 4)
                : [];
            const messageLineHeight = 16;
            const textBlockHeight = wrapped.length * lineHeight + (messageLines.length ? 4 + messageLines.length * messageLineHeight : 0);
            const rowHeight = Math.max(avatarSize, textBlockHeight);
            // SVG text y is the baseline; visual mid of the first line is ~0.35em above it.
            const avatarCenterY = cursorY - fontSize * 0.35;
            lineSvgs.push(CommonFileUtils.renderPersonAvatar(entry.personId ? this.findPerson(people, entry.personId) : null, x, avatarCenterY, avatarSize, `summary-avatar-clip-${idOffset + rowIndex}`, defs));
            for (let i = 0; i < wrapped.length; i++) {
                lineSvgs.push(`<text x="${textX}" y="${cursorY + i * lineHeight}" font-family="${this.FONT}" font-size="${fontSize}" fill="#222222">${this.escapeXml(wrapped[i])}</text>`);
            }
            if (messageLines.length) {
                const messageStartY = cursorY + wrapped.length * lineHeight + 4;
                for (let i = 0; i < messageLines.length; i++) {
                    lineSvgs.push(`<text x="${textX}" y="${messageStartY + i * messageLineHeight}" font-family="${this.FONT}" font-size="${this.FEED_TEXT_SIZE}" font-style="italic" fill="#4b4b4b">${this.escapeXml(messageLines[i])}</text>`);
                }
            }
            cursorY += rowHeight + 10;
        }
        if (!sentences.length) {
            lineSvgs.push(`<text x="${x}" y="${cursorY}" font-family="${this.FONT}" font-size="${fontSize}" fill="#5a5a5a">No workflow activity.</text>`);
            cursorY += lineHeight;
        }
        const defsSvg = defs.length ? `<defs>\n  ${defs.join('\n  ')}\n  </defs>` : '';
        const titleSvg = showTitle
            ? `<text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="19" font-weight="bold" fill="#111111">Workflow</text>`
            : '';
        return {
            svg: `${defsSvg}
  ${titleSvg}
  ${lineSvgs.join('\n  ')}`,
            endY: cursorY,
        };
    }
    /** Photos only render when a high-res image or thumb is available. */
    static hasPhotoImage(photo, photoImages = {}) {
        return !!(photoImages[photo._id] || photo?.thumb);
    }
    static getRenderablePhotoEntries(photos, people = [], photoImages = {}) {
        return photos
            .filter((photo) => this.hasPhotoImage(photo, photoImages))
            .map((photo) => {
            const base64 = photoImages[photo._id] || photo.thumb;
            const thumbWidth = photo?.thumb_width && photo.thumb_width > 0 ? photo.thumb_width : 160;
            const thumbHeight = photo?.thumb_height && photo.thumb_height > 0 ? photo.thumb_height : 120;
            const timestamp = this.formatPhotoCaptionDate(photo?.created_date);
            const person = photo?.created_by ? this.findPerson(people, photo.created_by) : null;
            return { base64, aspectRatio: thumbWidth / thumbHeight, timestamp, person };
        });
    }
    static renderPhotoGrid(photos, x, startY, maxWidth) {
        if (!photos.length) {
            return `<text x="${x}" y="${startY + 24}" font-family="${this.FONT}" font-size="12" fill="#5a5a5a">No photo attachments.</text>`;
        }
        const gap = this.PHOTO_GAP;
        const maxY = CommonFileUtils.FOOTER_RULE_Y;
        const availHeight = Math.max(0, maxY - startY);
        const count = photos.length;
        // Size photos by column width (aspect-correct height), pack rows from the top with only `gap`
        // between them. Pick the column count that maximizes drawn area while fitting the page.
        let best = null;
        for (let cols = 1; cols <= count; cols++) {
            const rows = Math.ceil(count / cols);
            const cellWidth = Math.floor((maxWidth - gap * (cols - 1)) / cols);
            if (cellWidth <= 0)
                continue;
            let contentHeight = gap * (rows - 1);
            const rowHeights = [];
            for (let row = 0; row < rows; row++) {
                let rowHeight = 0;
                for (let col = 0; col < cols; col++) {
                    const photo = photos[row * cols + col];
                    if (!photo)
                        break;
                    rowHeight = Math.max(rowHeight, Math.round(cellWidth / photo.aspectRatio));
                }
                rowHeights.push(rowHeight);
                contentHeight += rowHeight;
            }
            const scale = contentHeight > availHeight ? availHeight / contentHeight : 1;
            const area = photos.reduce((sum, photo) => {
                const drawW = cellWidth * scale;
                const drawH = (cellWidth / photo.aspectRatio) * scale;
                return sum + drawW * drawH;
            }, 0);
            if (!best || area > best.area) {
                best = { cols, scale, area };
            }
        }
        if (!best) {
            return `<text x="${x}" y="${startY + 24}" font-family="${this.FONT}" font-size="12" fill="#5a5a5a">No photo attachments.</text>`;
        }
        const { cols, scale } = best;
        const cellWidth = Math.floor((maxWidth - gap * (cols - 1)) / cols);
        const parts = [];
        const defs = [];
        let cursorY = startY;
        let photoIndex = 0;
        for (let row = 0; row < Math.ceil(count / cols); row++) {
            let rowHeight = 0;
            const rowItems = [];
            for (let col = 0; col < cols; col++) {
                const photo = photos[row * cols + col];
                if (!photo)
                    break;
                const drawWidth = Math.round(cellWidth * scale);
                const drawHeight = Math.round((cellWidth / photo.aspectRatio) * scale);
                rowHeight = Math.max(rowHeight, drawHeight);
                rowItems.push({
                    photo,
                    drawX: x + col * (cellWidth + gap) + Math.floor((cellWidth - drawWidth) / 2),
                    drawWidth,
                    drawHeight,
                });
            }
            for (const item of rowItems) {
                const drawY = cursorY; // pack from top of the row — no vertical centering padding
                const dataUri = CommonFileUtils.getImageDataUri(item.photo.base64);
                parts.push(`<image href="${dataUri}" x="${item.drawX}" y="${drawY}" width="${item.drawWidth}" height="${item.drawHeight}" preserveAspectRatio="none"/>`);
                const caption = this.renderPhotoCaption(item.photo, item.drawX, drawY, item.drawWidth, item.drawHeight, photoIndex, defs);
                if (caption)
                    parts.push(caption);
                photoIndex += 1;
            }
            cursorY += rowHeight + gap;
        }
        const defsSvg = defs.length ? `<defs>\n  ${defs.join('\n  ')}\n  </defs>\n  ` : '';
        return `${defsSvg}${parts.join('\n  ')}`;
    }
    /**
     * Bottom-left overlay: avatar + two-line caption (name, then date/time).
     * Matches the file-badge avatar/text treatment.
     */
    static renderPhotoCaption(photo, drawX, drawY, drawWidth, drawHeight, index, defsOut) {
        const name = photo.person ? this.getFullName(photo.person).trim() : 'Unknown';
        const timestamp = photo.timestamp || '';
        if (!name && !timestamp)
            return '';
        const pad = 4;
        const avatarSize = 22;
        const avatarGap = 6;
        const fontSize = 9;
        const lineHeight = 11;
        const overlayHeight = Math.max(avatarSize, lineHeight * 2) + pad * 2;
        const overlayX = drawX + pad;
        const overlayY = drawY + drawHeight - overlayHeight - pad;
        const avatarX = overlayX + pad;
        const avatarCenterY = overlayY + overlayHeight / 2;
        const textX = avatarX + avatarSize + avatarGap;
        const nameY = overlayY + pad + fontSize;
        const dateY = nameY + lineHeight;
        const avatarSvg = CommonFileUtils.renderPersonAvatar(photo.person, avatarX, avatarCenterY, avatarSize, `photo-avatar-clip-${index}`, defsOut);
        return `<rect x="${overlayX}" y="${overlayY}" width="${drawWidth - pad * 2}" height="${overlayHeight}" fill="#ffffff" fill-opacity="0.75"/>
  ${avatarSvg}
  <text x="${textX}" y="${nameY}" font-family="${this.FONT}" font-size="${fontSize}" font-weight="bold" fill="#333333">${this.escapeXml(name)}</text>
  <text x="${textX}" y="${dateY}" font-family="${this.FONT}" font-size="${fontSize}" fill="#555555">${this.escapeXml(timestamp)}</text>`;
    }
    /** e.g. `Jan 18 2021 09:11am` */
    static formatPhotoCaptionDate(iso) {
        const date = DateUtils.fromISO(iso);
        if (!date)
            return '';
        const datePart = DateUtils.format(date);
        const timePart = date
            .toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
            .replace(/\s/g, '')
            .toLowerCase();
        return `${datePart} ${timePart}`.trim();
    }
    static truncateText(value, maxChars) {
        if (!value)
            return '';
        if (value.length <= maxChars)
            return value;
        return `${value.slice(0, Math.max(1, maxChars - 1)).trimEnd()}...`;
    }
    static formatPersonWithCompany(people, personId) {
        const person = this.findPerson(people, personId);
        if (!person)
            return '';
        const name = this.getFullName(person);
        return person.company ? `${name} (${person.company})` : name;
    }
    static formatPeopleList(people, personIds) {
        if (!personIds || personIds.length === 0)
            return 'No-one';
        return (personIds ?? [])
            .map((personId) => this.formatPersonWithCompany(people, personId))
            .filter(Boolean)
            .join(', ');
    }
    /**
     * Avg glyph width as a fraction of fontSize for Arial/Helvetica body text.
     * Chromium measures ~0.42–0.44 for typical prose; 0.55 was far too conservative
     * (wrapping left ~15–20% empty). Stay slightly above measured to avoid overflow
     * on wider glyphs. Not shared with MarkupCommonUtils (markups keep a safer 0.55).
     */
    static WRAP_CHAR_WIDTH_RATIO = 0.46;
    static wrapText(text, maxWidth, fontSize) {
        if (!text)
            return [];
        const estimateWidth = (value) => value.length * fontSize * this.WRAP_CHAR_WIDTH_RATIO;
        const paragraphs = text.split(/\r?\n/);
        const lines = [];
        for (const paragraph of paragraphs) {
            const words = paragraph.split(/\s+/).filter(Boolean);
            if (words.length === 0) {
                lines.push('');
                continue;
            }
            let current = words[0];
            for (let i = 1; i < words.length; i++) {
                const candidate = `${current} ${words[i]}`;
                if (estimateWidth(candidate) <= maxWidth) {
                    current = candidate;
                }
                else {
                    lines.push(current);
                    current = words[i];
                }
            }
            lines.push(current);
        }
        return lines;
    }
    static escapeXml(text) {
        if (!text)
            return '';
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    static findPerson(people, personId) {
        return people.find((person) => person._id === personId) ?? null;
    }
    static getFullName(person) {
        return `${person.first_name} ${person.last_name}`;
    }
}
